import React, { useEffect, useCallback, useState } from 'react';
import store from 'redux/configureStore';
import { useDispatch, useSelector } from 'react-redux';
import { notification, theme, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ContainerOutlined } from '@ant-design/icons';
import rest from 'util/Api';
import dayjs from 'dayjs';
import { DEFAULT_PAGINATE } from 'constants/defaultValues';
import API from 'constants/api';
import { ExportDateFormat } from 'constants/dateFormats';
import CsvDownloader from 'util/CsvDownloader';
import PaqueryTable, { TableHeader } from '@paquery-team/lib-table';
import DeleteModal from 'components/deleteModal';
import usePaquerypoints from 'redux/paquerypoint';
import { actions } from 'redux/paquerypoint/slice';
import {
  paquerypointsLoadedSelector,
  paquerypointsItemsSelector,
  paquerypointsFilteredItemsSelector,
} from 'redux/paquerypoint/selectors';
import EditRecordButton from 'components/editRecordButton';
import DeleteRecordButton from 'components/deleteRecordButton';

const ViewPackagesButton = ({ paquerypointId }) => {
  const { token } = theme.useToken();
  return (
    <Tooltip title="Ver paquetes">
      <Link
        to={`/paquerypoints/${paquerypointId}?tab=packages`}
        aria-label="Ver paquetes"
      >
        <ContainerOutlined
          style={{ color: token.colorPrimary, fontSize: 18 }}
        />
      </Link>
    </Tooltip>
  );
};

ViewPackagesButton.propTypes = {
  paquerypointId: PropTypes.number.isRequired,
};

const deleteAction = (paqueryPointId, paqueryPointName) => {
  const deleteRequest = async () => {
    let response;
    try {
      response = await rest.del(`${API.paqueryPoint.create}/${paqueryPointId}`);
      if (rest.isSuccessResponse(response)) {
        store.dispatch(actions.refreshPage());
      }
    } catch (error) {
      console.error(error);
      notification.error({
        message: 'Ha ocurrido un error al desactivar el Paquery Point',
        description:
          'Intente nuevamente por favor o informelo a los administradores',
      });
    }
    return response;
  };
  DeleteModal({
    title: `¿Estás seguro de que deseas desactivar el PaQuery Point ${paqueryPointName}?`,
    onConfirmation: deleteRequest,
    successMessage: 'Desactivación completada',
    successDescription: `El PaQuery Point ${paqueryPointName} quedó inactivo. Podés volver a activarlo desde Editar.`,
  });
};

const viewPackagesColumn = {
  title: 'Ver paquetes',
  align: 'center',
  render: (_, record) => <ViewPackagesButton paquerypointId={record.key} />,
};

const editPaquerypointColumn = {
  title: 'Editar',
  align: 'center',
  render: (_, record) => (
    <EditRecordButton record={record} link="/paquerypoints" />
  ),
};

const deletePaquerypointColumn = {
  title: 'Desactivar',
  align: 'center',
  render: (_, record) =>
    record.active === 'Activo' && (
      <DeleteRecordButton
        onClick={() => deleteAction(record.key, record.name)}
      />
    ),
};

const nameColumn = {
  title: 'Nombre',
  dataIndex: 'name',
};

const detailColumn = {
  title: 'Descripción',
  dataIndex: 'detail',
};

const phoneColumn = {
  title: 'Teléfono',
  dataIndex: 'phone',
};

const contactNameColumn = {
  title: 'Contacto',
  dataIndex: 'contactName',
};

const activeColumn = {
  title: 'Estado',
  dataIndex: 'active',
};

const dataColumns = [
  nameColumn,
  detailColumn,
  phoneColumn,
  contactNameColumn,
  activeColumn,
];

const columnsLargeDevice = [
  ...dataColumns,
  viewPackagesColumn,
  editPaquerypointColumn,
  deletePaquerypointColumn,
];

const columnsMediumDevice = [...dataColumns];

const columnsSmallDevice = [nameColumn, contactNameColumn];

const PaqueryPointList = () => {
  usePaquerypoints();
  const dispatch = useDispatch();
  const loaded = useSelector(paquerypointsLoadedSelector);
  const fullItems = useSelector(paquerypointsItemsSelector);
  const { items, pageable } = useSelector(paquerypointsFilteredItemsSelector);
  const [disabled, setDisabled] = useState(false);
  useEffect(() => {
    dispatch(actions.initial('ALL'));
  }, [dispatch]);
  const getCsv = async () => {
    setDisabled(true);
    try {
      const csv = fullItems.map((paqueryPoint) => ({
        ...paqueryPoint,
        active: paqueryPoint.active ? 'Si' : 'No',
        published: paqueryPoint.published ? 'Si' : 'No',
        deleted: paqueryPoint.deleted ? 'Si' : 'No',
        private: paqueryPoint.private ? 'Si' : 'No',
        openBranchOffice: paqueryPoint.openBranchOffice ? 'Si' : 'No',
      }));
      const csvHeaders = [
        { label: 'ID', value: 'id' },
        { label: 'Nombre', value: 'name' },
        { label: 'Descripción', value: 'detail' },
        { label: 'Dirección', value: 'address' },
        { label: 'Contacto', value: 'contactName' },
        { label: 'Activo', value: 'active' },
        { label: 'Publicado', value: 'published' },
        { label: 'Privado', value: 'private' },
        { label: 'Eliminado', value: 'deleted' },
        { label: 'Volumen', value: 'volume' },
        { label: 'Sucursal Abierta', value: 'openBranchOffice' },
        { label: 'Id de Opl dueño', value: 'ownerLogisticOperatorId' },
      ];
      CsvDownloader(
        csv,
        csvHeaders,
        `paquerypoints-${dayjs().tz().format(ExportDateFormat)}.csv`,
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      notification.error({
        message: 'Ha ocurrido un error al traer los paquetes',
        description:
          'Intente nuevamente por favor o informelo a los administradores',
      });
    } finally {
      setDisabled(false);
    }
  };
  let filteredPaquerypoints;

  if (loaded && items) {
    filteredPaquerypoints = items.map((paqueryPoint) => ({
      ...paqueryPoint,
      key: paqueryPoint.id,
      name: paqueryPoint.name,
      detail: paqueryPoint.detail,
      phone: paqueryPoint.phone,
      contactName: paqueryPoint.contactName,
      active: paqueryPoint.active ? 'Activo' : 'Inactivo',
    }));
  }

  const searchCallback = useCallback(
    (searchText) => {
      dispatch(actions.updateSearch(searchText));
    },
    [dispatch],
  );
  const searcher = {
    key: 'searchCodeOrDestinationName',
    onSearching: searchCallback,
    placeholder: 'Nombre',
    allowEmptySearch: true,
  };

  const updatePaginate = useCallback(
    (page) => {
      dispatch(actions.updatePageable(page));
    },
    [dispatch],
  );
  return (
    <PaqueryTable
      loading={!loaded}
      header={{
        title: 'PaQuery Points',
        searcher,
        selectors: [
          {
            placeholder: 'Estado',
            list: [
              { name: 'Activos', value: 'ACTIVE' },
              { name: 'Inactivos', value: 'INACTIVE' },
            ],
            onChange: (value) => dispatch(actions.updateStatus(value || 'ALL')),
          },
        ],
        onExportCsv: { callback: getCsv, disabled },
        refresh: () => dispatch(actions.refreshPage()),
        primaryButton: <TableHeader.AddButton url="/paquerypoints/add" />,
      }}
      onChangePaginate={updatePaginate}
      dataSource={filteredPaquerypoints}
      paginate={pageable || DEFAULT_PAGINATE}
      dataColumns={dataColumns}
      colsForSmallDevice={columnsSmallDevice}
      colsForMediumDevice={columnsMediumDevice}
      colsForLargeDevice={columnsLargeDevice}
    />
  );
};

export default PaqueryPointList;
